import type { FocusEvent } from 'react'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'

/**
 * Shell regions for tour welcome + narrative deck.
 * Names align with style-guide 2b / surface inventory (icon rail, mode panel).
 * Secondary/peek is optional per view - folded into Main, not a separate hotspot.
 */
export const PRODUCT_TOUR_SHELL_REGIONS = [
  {
    id: 'header',
    label: 'Header',
    blurb: 'Wordmark, Search, profile, panel toggles, and Help.',
    testId: testIds.header.root,
  },
  {
    id: 'icon-bar',
    label: 'Icon rail',
    blurb:
      'Browse, Inbox, Flows, and Chats, plus extension icons below the divider. Settings at the bottom.',
    testId: testIds.leftSidebar.iconBar,
  },
  {
    id: 'sidebar',
    label: 'Mode panel',
    blurb:
      'The list beside the icons follows the icon you pick.',
    testId: testIds.leftSidebar.panel,
  },
  {
    id: 'main',
    label: 'Main panel',
    blurb:
      'Tabs and the active view - Home, documents, search, inbox threads. Some views also open an optional peek (Document Info, preview) beside the content.',
    testId: testIds.resizableLayout.mainArea,
  },
  {
    id: 'peek',
    label: 'Secondary panel',
    blurb: 'An optional peek beside the content. Hover the dashed slice to open it, and move away to close it.',
    testId: testIds.home.previewPane,
  },
  {
    id: 'agent',
    label: 'Agent panel',
    blurb: 'Ask about the document you have open.',
    testId: testIds.resizableLayout.chatPane,
  },
] as const

export type ProductTourShellRegionId = (typeof PRODUCT_TOUR_SHELL_REGIONS)[number]['id']

type ProductTourAnatomyDiagramProps = {
  className?: string
  /** Controlled hover/focus region - parent spotlights live chrome. */
  activeId?: ProductTourShellRegionId | null
  onActiveChange?: (id: ProductTourShellRegionId | null) => void
}

/**
 * Shell wireframe for tour welcome (and narrative deck reuse).
 * Hover/focus a region to highlight it, show its name, and illuminate live UI.
 */
export function ProductTourAnatomyDiagram({
  className,
  activeId = null,
  onActiveChange,
}: ProductTourAnatomyDiagramProps) {
  const setActive = (id: ProductTourShellRegionId | null) => {
    onActiveChange?.(id)
  }
  const activeMeta = PRODUCT_TOUR_SHELL_REGIONS.find((r) => r.id === activeId)

  const clearIfLeft = (event: FocusEvent<SVGRectElement>) => {
    const next = event.relatedTarget
    if (next instanceof Node && event.currentTarget.ownerSVGElement?.contains(next)) return
    setActive(null)
  }

  const regionClass = (id: ProductTourShellRegionId) =>
    cn(
      'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
      'transition-[fill,stroke] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
      activeId === id
        ? 'fill-primary/25 stroke-primary/70'
        : activeId
          ? 'fill-muted/30 stroke-border'
          : 'fill-muted/50 stroke-border',
    )

  return (
    <div
      className={cn('space-y-2.5', className)}
      data-testid={testIds.productTour.anatomy}
    >
      <svg
        viewBox="0 0 320 148"
        className="w-full h-auto min-h-[148px] rounded-md border border-border/60 bg-background"
        role="img"
        aria-label="Maater shell anatomy: header, icon rail, mode panel, main panel, agent panel"
        onMouseLeave={() => setActive(null)}
      >
        {/* Header */}
        <rect
          x="6"
          y="6"
          width="308"
          height="18"
          rx="2"
          strokeWidth="1.5"
          className={regionClass('header')}
          tabIndex={0}
          role="button"
          aria-label="Header"
          data-testid={testIds.productTour.anatomyRegion('header')}
          onMouseEnter={() => setActive('header')}
          onFocus={() => setActive('header')}
          onBlur={clearIfLeft}
        />
        <text
          x="14"
          y="18"
          className="fill-muted-foreground pointer-events-none"
          style={{ fontSize: 9, fontWeight: 600 }}
        >
          Header
        </text>

        {/* Icon rail */}
        <rect
          x="6"
          y="30"
          width="22"
          height="112"
          rx="2"
          strokeWidth="1.5"
          className={regionClass('icon-bar')}
          tabIndex={0}
          role="button"
          aria-label="Icon rail"
          data-testid={testIds.productTour.anatomyRegion('icon-bar')}
          onMouseEnter={() => setActive('icon-bar')}
          onFocus={() => setActive('icon-bar')}
          onBlur={clearIfLeft}
        />

        {/* Mode panel (sidebar) */}
        <rect
          x="34"
          y="30"
          width="64"
          height="112"
          rx="2"
          strokeWidth="1.5"
          className={regionClass('sidebar')}
          tabIndex={0}
          role="button"
          aria-label="Mode panel"
          data-testid={testIds.productTour.anatomyRegion('sidebar')}
          onMouseEnter={() => setActive('sidebar')}
          onFocus={() => setActive('sidebar')}
          onBlur={clearIfLeft}
        />

        {/* Main panel. Dashed slice is its own target so hover can open the live peek. */}
        <rect
          x="104"
          y="30"
          width="124"
          height="112"
          rx="2"
          strokeWidth="1.5"
          className={regionClass('main')}
          tabIndex={0}
          role="button"
          aria-label="Main panel"
          data-testid={testIds.productTour.anatomyRegion('main')}
          onMouseEnter={() => setActive('main')}
          onFocus={() => setActive('main')}
          onBlur={clearIfLeft}
        />
        <rect
          x="188"
          y="42"
          width="32"
          height="88"
          rx="1.5"
          strokeWidth="1.5"
          strokeDasharray="3 2"
          className={cn(
            'cursor-pointer outline-none transition-[fill,stroke] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
            activeId === 'peek'
              ? 'fill-primary/30 stroke-primary'
              : 'fill-transparent stroke-muted-foreground/45 hover:fill-primary/15 hover:stroke-primary/70',
          )}
          tabIndex={0}
          role="button"
          aria-label="Secondary panel"
          data-testid={testIds.productTour.anatomyRegion('peek')}
          onMouseEnter={() => setActive('peek')}
          onFocus={() => setActive('peek')}
          onBlur={clearIfLeft}
        />

        {/* Agent panel */}
        <rect
          x="234"
          y="30"
          width="80"
          height="112"
          rx="2"
          strokeWidth="1.5"
          className={regionClass('agent')}
          tabIndex={0}
          role="button"
          aria-label="Agent panel"
          data-testid={testIds.productTour.anatomyRegion('agent')}
          onMouseEnter={() => setActive('agent')}
          onFocus={() => setActive('agent')}
          onBlur={clearIfLeft}
        />
      </svg>

      <div
        className="h-24 overflow-hidden rounded-md border border-border/50 bg-muted/30 px-3 py-2"
        data-testid={testIds.productTour.anatomyCaption}
        aria-live="polite"
      >
        <div
          key={activeId ?? 'idle'}
          className="animate-in fade-in duration-300"
        >
          {activeMeta ? (
            <>
              <p className="text-sm font-semibold text-foreground">{activeMeta.label}</p>
              <p className="text-xs text-muted-foreground leading-snug mt-0.5">{activeMeta.blurb}</p>
            </>
          ) : (
            <p className="text-xs text-muted-foreground leading-snug">
              Hover a region to light it in the live app. The dashed slice in Main opens the secondary panel.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

export function productTourRegionTestId(
  id: ProductTourShellRegionId,
): string {
  const row = PRODUCT_TOUR_SHELL_REGIONS.find((r) => r.id === id)
  return row?.testId ?? ''
}
